import type {
  SourceEntityMapping,
  SourceFieldMapping,
  SourceIdentityRule,
  SourceMappingProfile,
  SourceRelationMapping,
  SourceTombstonePolicy,
} from '../models';
import { withPositionTitleCompatibility } from './SourceMappingProfileCompatibility';
import {
  sourcePositionRowContract,
  vacantSeatReferencePositionId,
  type SourcePositionRowContract,
} from './SourceMappingRowContract';
import { authorityTypesForIntent } from '../../../core/data-profiles/sourceAuthorityTypes';
import {
  SHARED_RELATION_PREDICATE_PACK,
  validateRelationContract,
  type RelationContractValidationCode,
} from '../../../core/ontology/RelationPredicateRegistry';

export interface SourceMappingSourceRecord {
  readonly id: string;
  readonly data: Record<string, unknown>;
}

export interface SourceMappingMaterializedRecord {
  readonly externalId: string | null;
  readonly profileId?: string;
  readonly universalProps: Record<string, unknown>;
  readonly tombstonePolicy?: SourceTombstonePolicy;
}

export interface SourceMappingIdentityClaim {
  readonly ruleId: string;
  readonly strategy: SourceIdentityRule['strategy'];
  readonly sourceSystem: string;
  readonly intent: SourceMappingProfile['intent'];
  readonly sourceRecordId: string;
  readonly entityRef: string;
  readonly entityType: string;
  readonly profileId: string;
  readonly entityExternalId: string;
  readonly label: string;
  readonly fields: readonly string[];
  readonly values: readonly string[];
  readonly targetProfileId?: string;
  readonly confidence: number | null;
  /** Resolve only as a relationship endpoint; never create a canonical entity or durable alias. */
  readonly referenceOnly?: boolean;
}

export interface SourceMappingUnresolvedIssue {
  readonly sourceRecordId: string;
  readonly mappingId: string;
  readonly kind: 'entity' | 'relation';
  readonly reason:
    | 'missing_required_field'
    | 'missing_entity'
    | 'ambiguous_reference'
    | 'unresolved_reference'
    | 'conflicting_duplicate';
  readonly missingFields?: readonly string[];
  readonly missingEntityRefs?: readonly string[];
}

export interface SourceMappingMaterializeInput {
  readonly mappingProfile: SourceMappingProfile;
  readonly records: readonly SourceMappingSourceRecord[];
  /**
   * Full source snapshot for intra-file reference resolution when
   * `records` is one batch of a larger run. Reference values (e.g. a
   * manager name) are matched against this scope, so adoption and
   * ambiguity detection see the whole run rather than one batch —
   * otherwise duplicate labels split across batches would resolve
   * "uniquely" to the wrong row. Defaults to `records`.
   */
  readonly referenceScopeRecords?: readonly SourceMappingSourceRecord[];
  /** Prepared once per import run to avoid rebuilding the whole-file index for every batch. */
  readonly referenceResolver?: SourceMappingReferenceResolver;
  /**
   * Which seat is each person's primary one when the source gives them
   * several. Prepared over the whole run like the reference resolver; built
   * from `referenceScopeRecords` when absent.
   */
  readonly primaryOccupancy?: SourceMappingPrimaryOccupancyPlan;
  readonly connectorId?: string;
  readonly sourceLabel?: string;
}

export interface SourceMappingMaterializeResult {
  readonly profileId: string;
  readonly records: readonly SourceMappingMaterializedRecord[];
  readonly entityCount: number;
  readonly relationCount: number;
  readonly unresolvedCount: number;
  readonly unresolvedIssues: readonly SourceMappingUnresolvedIssue[];
  readonly identityClaims: readonly SourceMappingIdentityClaim[];
  /**
   * Distinct reference entities (e.g. a manager named in a cell) that matched
   * no row in the source and were therefore minted as label-keyed records.
   * These are real persisted people/seats with no row of their own, so runs
   * surface the count for review instead of succeeding silently.
   */
  readonly mintedReferenceCount: number;
}

export interface SourceMappingRelationContractIssue {
  readonly mappingId: string;
  readonly predicate: string;
  readonly code: RelationContractValidationCode | 'missing_entity_mapping' | 'missing_relation_mapping' | 'unstable_relation_identity';
  readonly message: string;
}

export class SourceMappingRelationContractError extends Error {
  constructor(readonly issues: readonly SourceMappingRelationContractIssue[]) {
    super('source_mapping_relation_contract_invalid');
    this.name = 'SourceMappingRelationContractError';
  }
}

export const RELATIONSHIP_RECORD_PROFILE_ID = 'relationship';

export type SourceMappingReferenceResolver = (
  entityMappingId: string,
  sourceRecord: SourceMappingSourceRecord,
) => IntraSourceReferenceResolution | null;

/** Person external id → external id of the seat that counts as their primary one. */
export type SourceMappingPrimaryOccupancyPlan = ReadonlyMap<string, string>;

const SECONDARY_ASSIGNMENT_TYPE = 'secondary';

interface MaterializedEntity {
  readonly entityRef: string;
  readonly externalId: string;
  /**
   * True when this entity is a same-file reference to another row's
   * entity (e.g. a manager column naming a person row). Reference stubs
   * serve as relation endpoints only and are never emitted as records, so
   * they cannot overwrite the referenced row's fields in any batch order.
   */
  readonly referenceStub: boolean;
  readonly record: SourceMappingMaterializedRecord;
  readonly identityClaims: readonly SourceMappingIdentityClaim[];
}

interface MaterializedEdge {
  readonly id: string;
  readonly from: string;
  readonly to: string;
  readonly kind: string;
  readonly attributes: Record<string, unknown>;
}

interface MaterializedRelation {
  readonly edge: MaterializedEdge;
  readonly record: SourceMappingMaterializedRecord;
}

/**
 * Converts a confirmed SourceMappingProfile into the existing folder_records
 * substrate: ontology-shaped node records plus the established synthetic edge
 * record (`_isEdgeRecord` + `_edges`) consumed by permissioned repository and
 * federation projections.
 */
export class SourceMappingMaterializer {
  prepareReferenceResolverAccumulator(
    mappingProfile: SourceMappingProfile,
  ): SourceMappingReferenceResolverAccumulator {
    return new SourceMappingReferenceResolverAccumulator(
      withPositionTitleCompatibility(mappingProfile),
    );
  }

  prepareReferenceResolver(
    mappingProfile: SourceMappingProfile,
    scopeRecords: readonly SourceMappingSourceRecord[],
  ): SourceMappingReferenceResolver {
    const accumulator = this.prepareReferenceResolverAccumulator(mappingProfile);
    accumulator.add(scopeRecords);
    return accumulator.resolver();
  }

  materialize(rawInput: SourceMappingMaterializeInput): SourceMappingMaterializeResult {
    const mappingProfile = withPositionTitleCompatibility(rawInput.mappingProfile);
    const input = mappingProfile === rawInput.mappingProfile
      ? rawInput
      : { ...rawInput, mappingProfile };
    assertSourceMappingRelationContracts(mappingProfile);
    const positionRow = sourcePositionRowContract(mappingProfile);
    const entityRecordsByExternalId = new Map<string, SourceMappingMaterializedRecord>();
    const relationshipRecordsByExternalId = new Map<string, SourceMappingMaterializedRecord>();
    const edgesByRelationshipId = new Map<string, MaterializedEdge>();
    const blockedRelationshipIds = new Set<string>();
    const unresolvedIssues: SourceMappingUnresolvedIssue[] = [];
    const identityClaims: SourceMappingIdentityClaim[] = [];
    const mintedReferenceIds = new Set<string>();

    const entityMappings = mappingProfile.entityMappings.length > 0
      ? mappingProfile.entityMappings
      : [fallbackEntityMapping(mappingProfile)];
    const entityMappingsById = new Map(entityMappings.map((mapping) => [mapping.id, mapping]));
    const resolveReference = input.referenceResolver ?? buildIntraSourceReferenceResolver(
      mappingProfile,
      entityMappings,
      input.referenceScopeRecords ?? input.records,
    );
    const primaryOccupancy = input.primaryOccupancy ?? buildPrimaryOccupancyPlan(
      mappingProfile,
      entityMappings,
      input.referenceScopeRecords ?? input.records,
      resolveReference,
    );

    input.records.forEach((sourceRecord, rowIndex) => {
      const entitiesByRef = new Map<string, MaterializedEntity>();

      for (const entityMapping of entityMappings) {
        if (isOptionalEntityAbsent(mappingProfile, positionRow, entityMapping, sourceRecord)) continue;
        const missingFields = missingRequiredFields(entityMapping.fieldMappings, sourceRecord);
        if (missingFields.length > 0) {
          unresolvedIssues.push({
            sourceRecordId: sourceRecord.id,
            mappingId: entityMapping.id,
            kind: 'entity',
            reason: 'missing_required_field',
            missingFields,
          });
          continue;
        }
        const reference = resolveReference(entityMapping.id, sourceRecord);
        if (reference?.ambiguous) {
          unresolvedIssues.push({
            sourceRecordId: sourceRecord.id,
            mappingId: entityMapping.id,
            kind: 'entity',
            reason: 'ambiguous_reference',
            missingEntityRefs: [entityMapping.id],
          });
          // Ambiguous reference aliases are review evidence, not canonical
          // entities. Minting a label-derived manager/manager-position stub
          // here creates phantom people and seats that later appear to own the
          // hierarchy. The relation pass will also emit missing_entity and
          // remain safely unresolved until a reviewer selects an identity.
          continue;
        }
        if (reference?.unresolved) {
          // Minting a seat named after the reference would invent a phantom
          // vacant manager seat beside the real hierarchy.
          unresolvedIssues.push({
            sourceRecordId: sourceRecord.id,
            mappingId: entityMapping.id,
            kind: 'entity',
            reason: 'unresolved_reference',
            missingEntityRefs: [entityMapping.id],
          });
          continue;
        }
        const entity = this.materializeEntity(
          input,
          sourceRecord,
          rowIndex,
          entityMapping,
          reference?.externalId ?? null,
        );
        entitiesByRef.set(entity.entityRef, entity);
        identityClaims.push(...entity.identityClaims);
        if (reference?.unmatched && !entityMapping.referenceOnly) {
          mintedReferenceIds.add(entity.externalId);
        }
        // An adopted reference stub is an endpoint, not a record. The row it
        // adopted materializes the full entity in whichever batch holds it;
        // writing the stub as well would let a later batch replace that row's
        // persisted props with the label-only stub (the write gate replaces
        // universal props by default), so nothing is emitted for it here.
        if (!entityMapping.referenceOnly && !entity.referenceStub) {
          entityRecordsByExternalId.set(
            entity.externalId,
            mergeMaterializedRecord(entityRecordsByExternalId.get(entity.externalId), entity.record),
          );
        }
      }

      for (const relationMapping of input.mappingProfile.relationMappings) {
        const missingEntityRefs = [
          relationMapping.sourceEntityRef,
          relationMapping.targetEntityRef,
        ].filter((entityRef) => !entitiesByRef.has(entityRef));
        if (missingEntityRefs.length > 0) {
          const optionalSource = entityMappingsById.get(relationMapping.sourceEntityRef);
          const optionalTarget = entityMappingsById.get(relationMapping.targetEntityRef);
          const blankOptionalSource = isOptionalRelationSource(positionRow, relationMapping)
            && missingEntityRefs.length === 1
            && missingEntityRefs[0] === relationMapping.sourceEntityRef
            && optionalSource
            && isOptionalEntityAbsent(mappingProfile, positionRow, optionalSource, sourceRecord);
          const blankOptionalTarget = isOptionalRelationTarget(input.mappingProfile, relationMapping)
            && missingEntityRefs.length === 1
            && missingEntityRefs[0] === relationMapping.targetEntityRef
            && optionalTarget
            && isOptionalEntityAbsent(mappingProfile, positionRow, optionalTarget, sourceRecord);
          if (blankOptionalSource || blankOptionalTarget) continue;
          unresolvedIssues.push({
            sourceRecordId: sourceRecord.id,
            mappingId: relationMapping.id,
            kind: 'relation',
            reason: 'missing_entity',
            missingEntityRefs,
          });
          continue;
        }
        const missingFields = missingRequiredFields(
          relationMapping.qualifierMappings,
          sourceRecord,
        );
        if (missingFields.length > 0) {
          unresolvedIssues.push({
            sourceRecordId: sourceRecord.id,
            mappingId: relationMapping.id,
            kind: 'relation',
            reason: 'missing_required_field',
            missingFields,
          });
          continue;
        }
        if (isProtectedSuccessionRelation(relationMapping.predicate)) {
          const unstableRefs = [
            relationMapping.sourceEntityRef,
            relationMapping.targetEntityRef,
          ].filter((entityRef) =>
            !entitiesByRef.get(entityRef)?.identityClaims.length
          );
          if (unstableRefs.length > 0) {
            unresolvedIssues.push({
              sourceRecordId: sourceRecord.id,
              mappingId: relationMapping.id,
              kind: 'relation',
              reason: 'unresolved_reference',
              missingEntityRefs: unstableRefs,
            });
            continue;
          }
        }
        const relation = this.materializeRelation(
          input,
          sourceRecord,
          rowIndex,
          relationMapping,
          entitiesByRef,
          primaryOccupancy,
        );
        if (!relation) continue;
        if (relation.record.externalId) {
          const relationshipId = relation.record.externalId;
          if (blockedRelationshipIds.has(relationshipId)) {
            unresolvedIssues.push(conflictingRelationIssue(sourceRecord.id, relationMapping.id));
            continue;
          }
          const existing = relationshipRecordsByExternalId.get(relationshipId);
          if (existing && !sameRelationshipSemantics(existing, relation.record)) {
            relationshipRecordsByExternalId.delete(relationshipId);
            edgesByRelationshipId.delete(relationshipId);
            blockedRelationshipIds.add(relationshipId);
            unresolvedIssues.push(
              conflictingRelationIssue(materializedSourceRecordId(existing), relationMapping.id),
              conflictingRelationIssue(sourceRecord.id, relationMapping.id),
            );
            continue;
          }
          relationshipRecordsByExternalId.set(
            relationshipId,
            mergeDuplicateRelationshipRecord(existing, relation.record),
          );
          if (!existing) edgesByRelationshipId.set(relationshipId, relation.edge);
        }
      }
    });

    const edges = [...edgesByRelationshipId.values()];
    const records = Array.from(entityRecordsByExternalId.values());
    const syntheticEdges = edges
      .filter((edge) => !isProtectedSuccessionRelation(edge.kind))
      .map(withoutRowSnapshotEvidence);
    if (syntheticEdges.length > 0) {
      records.push({
        externalId: edgeRecordExternalId(input),
        universalProps: {
          _isEdgeRecord: true,
          _edges: syntheticEdges,
          source: sourceMetadata(input, null, null),
        },
      });
    }
    records.push(...relationshipRecordsByExternalId.values());

    return {
      profileId: input.mappingProfile.targetProfileId,
      records,
      entityCount: entityRecordsByExternalId.size,
      relationCount: edges.length,
      unresolvedCount: unresolvedIssues.length,
      unresolvedIssues,
      identityClaims,
      mintedReferenceCount: mintedReferenceIds.size,
    };
  }

  private materializeEntity(
    input: SourceMappingMaterializeInput,
    sourceRecord: SourceMappingSourceRecord,
    rowIndex: number,
    entityMapping: SourceEntityMapping,
    adoptedExternalId: string | null,
  ): MaterializedEntity {
    const externalId = adoptedExternalId ??
      entityExternalId(input.mappingProfile, entityMapping, sourceRecord, rowIndex);
    const props: Record<string, unknown> = {
      nodeType: entityMapping.ontologyType,
      _manifestId: entityMapping.ontologyType,
      source: sourceMetadata(input, sourceRecord, entityMapping.id),
    };

    for (const fieldMapping of entityMapping.fieldMappings) {
      const value = sourceValue(sourceRecord, fieldMapping.sourceField);
      if (value == null || value === '') continue;
      writeMappedValue(props, fieldMapping, value, 'props');
    }

    const label = entityLabel(entityMapping, sourceRecord, props, externalId);
    if (!hasString(props, 'name')) props.name = label;
    props._name = label;

    const identityClaims = identityClaimsForEntity({
      profile: input.mappingProfile,
      entityMapping,
      sourceRecord,
      externalId,
      label,
    });

    return {
      entityRef: entityMapping.id,
      externalId,
      referenceStub: adoptedExternalId != null,
      identityClaims,
      record: {
        externalId,
        profileId: entityMapping.profileId ?? input.mappingProfile.targetProfileId,
        universalProps: props,
        ...(entityMapping.tombstonePolicy
          ? { tombstonePolicy: entityMapping.tombstonePolicy }
          : {}),
      },
    };
  }

  private materializeRelation(
    input: SourceMappingMaterializeInput,
    sourceRecord: SourceMappingSourceRecord,
    rowIndex: number,
    relationMapping: SourceRelationMapping,
    entitiesByRef: ReadonlyMap<string, MaterializedEntity>,
    primaryOccupancy: SourceMappingPrimaryOccupancyPlan,
  ): MaterializedRelation | null {
    const sourceEntity = entitiesByRef.get(relationMapping.sourceEntityRef);
    const targetEntity = entitiesByRef.get(relationMapping.targetEntityRef);
    if (!sourceEntity || !targetEntity) return null;

    const attributes: Record<string, unknown> = {
      ...relationMapping.defaultQualifiers,
      source: sourceMetadata(input, sourceRecord, relationMapping.id),
    };
    for (const fieldMapping of relationMapping.qualifierMappings) {
      const value = sourceValue(sourceRecord, fieldMapping.sourceField);
      if (value == null || value === '') continue;
      writeMappedValue(attributes, fieldMapping, value, 'qualifiers');
    }
    // A person with several seats in one file has one primary seat; the
    // others are secondary. Left as primary they trip the concurrent-primary
    // occupancy check and the person chart cannot pick where to draw them.
    if (isPlannedPrimaryOccupancy(relationMapping)) {
      const primarySeat = primaryOccupancy.get(sourceEntity.externalId);
      if (primarySeat && primarySeat !== targetEntity.externalId) {
        attributes.assignmentType = SECONDARY_ASSIGNMENT_TYPE;
      }
    }

    const edge: MaterializedEdge = {
      id: relationMapping.identity === 'endpoints'
        ? [
            safeSegment(input.mappingProfile.id),
            safeSegment(relationMapping.id),
            stableSegment(sourceEntity.externalId),
            stableSegment(targetEntity.externalId),
          ].join(':')
        : [
            safeSegment(input.mappingProfile.id),
            safeSegment(relationMapping.id),
            stableSegment(sourceRecord.id || `row-${rowIndex + 1}`),
          ].join(':'),
      from: sourceEntity.externalId,
      to: targetEntity.externalId,
      kind: relationMapping.predicate,
      attributes,
    };

    return {
      edge,
      record: relationshipRecord(input, relationMapping, edge, sourceEntity, targetEntity),
    };
  }
}

/**
 * Retains only the identity, required, and label fields used by same-file
 * reference resolution. Wide raw rows can therefore be replayed from protected
 * staging without keeping every source column live for the whole run.
 */
export class SourceMappingReferenceResolverAccumulator {
  private readonly entityMappings: readonly SourceEntityMapping[];
  private readonly retainedFields: ReadonlySet<string>;
  private readonly scopeRecords: SourceMappingSourceRecord[] = [];

  constructor(private readonly mappingProfile: SourceMappingProfile) {
    assertSourceMappingRelationContracts(mappingProfile);
    this.entityMappings = mappingProfile.entityMappings.length > 0
      ? mappingProfile.entityMappings
      : [fallbackEntityMapping(mappingProfile)];
    const fteField = occupancyFteField(mappingProfile, this.entityMappings);
    this.retainedFields = new Set([
      ...mappingProfile.identityRules.flatMap((rule) => rule.fields),
      ...this.entityMappings.flatMap((mapping) => mapping.fieldMappings
        .filter((field) => field.required || field.role === 'label')
        .map((field) => field.sourceField)),
      // The primary-seat plan ranks a person's seats by FTE.
      ...(fteField ? [fteField] : []),
    ]);
  }

  add(records: readonly SourceMappingSourceRecord[]): void {
    for (const record of records) {
      const data: Record<string, unknown> = {};
      for (const field of this.retainedFields) {
        if (Object.prototype.hasOwnProperty.call(record.data, field)) {
          data[field] = record.data[field];
        }
      }
      this.scopeRecords.push({ id: record.id, data });
    }
  }

  resolver(): SourceMappingReferenceResolver {
    return buildIntraSourceReferenceResolver(
      this.mappingProfile,
      this.entityMappings,
      this.scopeRecords,
    );
  }

  primaryOccupancyPlan(): SourceMappingPrimaryOccupancyPlan {
    return buildPrimaryOccupancyPlan(
      this.mappingProfile,
      this.entityMappings,
      this.scopeRecords,
      this.resolver(),
    );
  }
}

function isPlannedPrimaryOccupancy(relationMapping: SourceRelationMapping): boolean {
  return relationMapping.predicate === 'occupies'
    && relationMapping.defaultQualifiers.assignmentType === 'primary'
    && !relationMapping.qualifierMappings.some((field) =>
      mappedQualifierKey(field.targetPath) === 'assignmentType');
}

function occupancyFteField(
  mappingProfile: SourceMappingProfile,
  entityMappings: readonly SourceEntityMapping[],
): string | null {
  const occupies = mappingProfile.relationMappings.find(isPlannedPrimaryOccupancy);
  if (!occupies) return null;
  const seatMapping = entityMappings.find((mapping) => mapping.id === occupies.targetEntityRef);
  return seatMapping?.fieldMappings.find((field) => field.targetPath === 'props.fte')?.sourceField
    ?? null;
}

/**
 * Picks one primary seat per person across the whole source. The primary
 * seat is where the people chart places the person, so it must be the seat
 * that carries the hierarchy: a seat that reports to one of the person's own
 * other seats is never primary (its reporting line would point at the person
 * themself and vanish, leaving them a root), the seat with the most direct
 * reports wins next, then the highest FTE, then the earliest row. Determined
 * over the full scope, so batching cannot change the answer.
 */
function buildPrimaryOccupancyPlan(
  mappingProfile: SourceMappingProfile,
  entityMappings: readonly SourceEntityMapping[],
  scopeRecords: readonly SourceMappingSourceRecord[],
  resolveReference: SourceMappingReferenceResolver,
): SourceMappingPrimaryOccupancyPlan {
  const occupies = mappingProfile.relationMappings.find(isPlannedPrimaryOccupancy);
  if (!occupies) return new Map();
  const personMapping = entityMappings.find((mapping) => mapping.id === occupies.sourceEntityRef);
  const seatMapping = entityMappings.find((mapping) => mapping.id === occupies.targetEntityRef);
  if (!personMapping || !seatMapping) return new Map();
  const fteField = occupancyFteField(mappingProfile, entityMappings);
  const positionRow = sourcePositionRowContract(mappingProfile);
  const reportsTo = mappingProfile.relationMappings.find((relation) =>
    relation.predicate === 'reports_to' && relation.sourceEntityRef === seatMapping.id);
  const managerMapping = reportsTo
    ? entityMappings.find((mapping) => mapping.id === reportsTo.targetEntityRef)
    : undefined;

  interface SeatFact { readonly seat: string; readonly manager: string | null; readonly fte: number; readonly row: number }
  const seatsByPerson = new Map<string, SeatFact[]>();
  const reportsBySeat = new Map<string, number>();
  scopeRecords.forEach((record, rowIndex) => {
    if (isOptionalEntityAbsent(mappingProfile, positionRow, seatMapping, record)) return;
    if (missingRequiredFields(seatMapping.fieldMappings, record).length > 0) return;
    const seat = entityExternalId(mappingProfile, seatMapping, record, rowIndex);
    let manager: string | null = null;
    if (managerMapping
      && !isOptionalEntityAbsent(mappingProfile, positionRow, managerMapping, record)
      && missingRequiredFields(managerMapping.fieldMappings, record).length === 0) {
      const resolution = resolveReference(managerMapping.id, record);
      manager = resolution?.externalId
        ?? (resolution?.ambiguous ? null : entityExternalId(mappingProfile, managerMapping, record, rowIndex));
      if (manager) reportsBySeat.set(manager, (reportsBySeat.get(manager) ?? 0) + 1);
    }
    if (isOptionalEntityAbsent(mappingProfile, positionRow, personMapping, record)) return;
    if (missingRequiredFields(personMapping.fieldMappings, record).length > 0) return;
    const person = entityExternalId(mappingProfile, personMapping, record, rowIndex);
    const fte = fteField ? Number(sourceValue(record, fteField)) : Number.NaN;
    seatsByPerson.set(person, [
      ...(seatsByPerson.get(person) ?? []),
      { seat, manager, fte: Number.isFinite(fte) ? fte : Number.NEGATIVE_INFINITY, row: rowIndex },
    ]);
  });

  const plan = new Map<string, string>();
  for (const [person, facts] of seatsByPerson) {
    const seats = new Set(facts.map((fact) => fact.seat));
    if (seats.size <= 1) continue;
    const ranked = [...facts].sort((a, b) => {
      const aSelf = a.manager !== null && seats.has(a.manager) ? 1 : 0;
      const bSelf = b.manager !== null && seats.has(b.manager) ? 1 : 0;
      if (aSelf !== bSelf) return aSelf - bSelf;
      const reports = (reportsBySeat.get(b.seat) ?? 0) - (reportsBySeat.get(a.seat) ?? 0);
      if (reports !== 0) return reports;
      if (a.fte !== b.fte) return b.fte - a.fte;
      return a.row - b.row;
    });
    plan.set(person, ranked[0]!.seat);
  }
  return plan;
}

/** Validates confirmed mappings before any row can materialize canonical facts. */
export function validateSourceMappingRelationContracts(
  profile: SourceMappingProfile,
): readonly SourceMappingRelationContractIssue[] {
  const entityMappings = new Map(profile.entityMappings.map((mapping) => [mapping.id, mapping]));
  const sourceTypes = authorityTypesForIntent(profile.intent);
  const issues: SourceMappingRelationContractIssue[] = [];

  for (const mapping of profile.relationMappings) {
    const subject = entityMappings.get(mapping.sourceEntityRef);
    const object = entityMappings.get(mapping.targetEntityRef);
    if (!subject || !object) {
      issues.push({
        mappingId: mapping.id,
        predicate: mapping.predicate,
        code: 'missing_entity_mapping',
        message: `Relation mapping '${mapping.id}' references an entity mapping that does not exist.`,
      });
      continue;
    }
    if (mapping.predicate === 'successor_of' && mapping.identity !== 'endpoints') {
      issues.push({
        mappingId: mapping.id,
        predicate: mapping.predicate,
        code: 'unstable_relation_identity',
        message: `Restricted relation mapping '${mapping.id}' must derive identity from stable endpoints.`,
      });
    }
    const qualifierKeys = [
      ...Object.keys(mapping.defaultQualifiers),
      ...mapping.qualifierMappings.map((field) => mappedQualifierKey(field.targetPath)),
    ].filter((key): key is string => Boolean(key));
    const result = validateRelationContract({
      predicate: mapping.predicate,
      subjectKind: subject.ontologyType,
      objectKind: object.ontologyType,
      qualifierKeys,
      sourceTypes,
      requireCompleteDefinition: true,
    });
    issues.push(...result.issues.map((issue) => ({
      mappingId: mapping.id,
      predicate: mapping.predicate,
      ...issue,
    })));
  }
  return issues;
}

export function assertSourceMappingRelationContracts(profile: SourceMappingProfile): void {
  const issues = validateSourceMappingRelationContracts(profile);
  if (issues.length > 0) throw new SourceMappingRelationContractError(issues);
}

function relationshipRecord(
  input: SourceMappingMaterializeInput,
  relationMapping: SourceRelationMapping,
  edge: MaterializedEdge,
  sourceEntity: MaterializedEntity,
  targetEntity: MaterializedEntity,
): SourceMappingMaterializedRecord {
  const qualifiers = normalizeRelationshipQualifiers(stripSource(edge.attributes));
  const targetKind = entityRecordTargetKind(targetEntity.record);
  return {
    profileId: RELATIONSHIP_RECORD_PROFILE_ID,
    externalId: relationshipExternalId(input, edge),
    universalProps: {
      nodeType: 'relationship',
      _manifestId: 'relationship',
      name: relationshipLabel(edge, sourceEntity, targetEntity),
      _name: relationshipLabel(edge, sourceEntity, targetEntity),
      relationshipType: edge.kind,
      predicate: edge.kind,
      subjectExternalId: edge.from,
      objectExternalId: edge.to,
      subjectProfileId: sourceEntity.record.profileId ?? input.mappingProfile.targetProfileId,
      objectProfileId: targetEntity.record.profileId ?? input.mappingProfile.targetProfileId,
      subjectLabel: entityRecordLabel(sourceEntity.record),
      objectLabel: entityRecordLabel(targetEntity.record),
      subjectType: entityRecordType(sourceEntity.record),
      objectType: entityRecordType(targetEntity.record),
      ...(targetKind ? { targetKind } : {}),
      sourceEntityRef: relationMapping.sourceEntityRef,
      targetEntityRef: relationMapping.targetEntityRef,
      mappingRef: relationMapping.id,
      ontologyLifecycle: validateRelationContract({ predicate: edge.kind }).lifecycle,
      ontologyRegistryVersion: SHARED_RELATION_PREDICATE_PACK.version,
      qualifiers,
      ...qualifiers,
      source: edge.attributes.source,
    },
    ...(relationMapping.tombstonePolicy
      ? { tombstonePolicy: relationMapping.tombstonePolicy }
      : {}),
  };
}

/**
 * Drops the row-snapshot evidence copy from an edge bound for the synthetic
 * `_edges` container.
 *
 * Every edge shares its `source` object with the relationship record built from
 * the same relation, so the snapshot is already persisted — once per
 * relationship record, and again per endpoint entity record. Repeating it a
 * fourth time inside `_edges` collapses one folder record into a single
 * multi-megabyte JSONB value: a 500-row org CSV produced a 3.5 MB `_edges`
 * blob, 32% of the whole import payload, which the import apply had to hold in
 * the write buffer, the history snapshot and the post-write reload at once.
 *
 * It is also the one copy nothing redacts. `withSanitizedEvidenceSnapshot`
 * strips protected columns from `universalProps.source.rowSnapshot` only; it
 * never descends into `_edges`, so the nested copy carried protected CSV
 * columns past field-level classification into the edge projections.
 *
 * No `_edges` consumer reads `attributes.source.rowSnapshot` — evidence
 * consumers (allocation, work context, resolution review, CSV rebuild) all read
 * it from the top-level record — so the projection loses nothing.
 */
function withoutRowSnapshotEvidence(edge: MaterializedEdge): MaterializedEdge {
  const source = edge.attributes.source;
  if (!source || typeof source !== 'object' || Array.isArray(source)) return edge;
  if (!('rowSnapshot' in (source as Record<string, unknown>))) return edge;
  const { rowSnapshot: _omitted, ...rest } = source as Record<string, unknown>;
  return { ...edge, attributes: { ...edge.attributes, source: rest } };
}

function isProtectedSuccessionRelation(predicate: string): boolean {
  return predicate === 'successor_of' || predicate === 'successor_for';
}

function mappedQualifierKey(targetPath: string): string | null {
  const normalized = targetPath.replace(/^(?:qualifiers|props|universalProps)\./, '');
  const segments = normalized.split('.').filter(Boolean);
  const key = segments[segments.length - 1];
  return key?.trim() || null;
}

function relationshipLabel(
  edge: MaterializedEdge,
  sourceEntity: MaterializedEntity,
  targetEntity: MaterializedEntity,
): string {
  return [
    entityRecordLabel(sourceEntity.record) ?? edge.from,
    humanizeIdentifier(edge.kind),
    entityRecordLabel(targetEntity.record) ?? edge.to,
  ].join(' ');
}

function entityRecordLabel(record: SourceMappingMaterializedRecord): string | undefined {
  const value = record.universalProps._name ?? record.universalProps.name;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function entityRecordType(record: SourceMappingMaterializedRecord): string | undefined {
  const value = record.universalProps.nodeType ?? record.universalProps._manifestId;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function entityRecordTargetKind(record: SourceMappingMaterializedRecord): string | undefined {
  const value = record.universalProps.targetKind;
  return typeof value === 'string' && value.trim() ? safeSegment(value) : undefined;
}

function fallbackEntityMapping(profile: SourceMappingProfile): SourceEntityMapping {
  return {
    id: 'row',
    sourceObject: 'row',
    ontologyType: 'artifact',
    profileId: profile.targetProfileId,
    optionalWhenBlank: false,
    referenceOnly: false,
    fieldMappings: [],
  };
}

function sourceMetadata(
  input: SourceMappingMaterializeInput,
  sourceRecord: SourceMappingSourceRecord | null,
  mappingRef: string | null,
): Record<string, unknown> {
  return {
    system: input.mappingProfile.sourceSystem,
    intent: input.mappingProfile.intent,
    mappingProfileId: input.mappingProfile.id,
    ...(input.connectorId ? { connectorId: input.connectorId } : {}),
    ...(input.sourceLabel ? { label: input.sourceLabel } : {}),
    ...(sourceRecord ? { recordId: sourceRecord.id } : {}),
    ...(mappingRef ? { mappingRef } : {}),
    ...(sourceRecord && input.mappingProfile.evidencePolicy.mode === 'row_snapshot'
      ? { rowSnapshot: sourceRecord.data }
      : {}),
  };
}

function entityExternalId(
  profile: SourceMappingProfile,
  entityMapping: SourceEntityMapping,
  sourceRecord: SourceMappingSourceRecord,
  rowIndex: number,
): string {
  const identityRule = profile.identityRules.find((rule) => rule.entityRef === entityMapping.id);
  const identityValues = identityRule?.fields
    .map((field) => sourceValue(sourceRecord, field))
    .filter((value): value is NonNullable<unknown> => value != null && value !== '');

  if (identityValues && identityValues.length > 0) {
    return [
      safeSegment(profile.sourceSystem),
      safeSegment(profile.intent),
      safeSegment(identityRule?.identityNamespace ?? entityMapping.id),
      ...identityValues.map(stableSegment),
    ].join(':');
  }

  const labelValue = entityMapping.fieldMappings
    .filter((field) => field.role === 'label' || field.required)
    .map((field) => sourceValue(sourceRecord, field.sourceField))
    .find((value) => value != null && value !== '');

  return [
    safeSegment(profile.sourceSystem),
    safeSegment(profile.intent),
    safeSegment(entityMapping.id),
    stableSegment(labelValue ?? sourceRecord.id ?? `row-${rowIndex + 1}`),
  ].join(':');
}

export interface IntraSourceReferenceResolution {
  readonly externalId: string | null;
  readonly ambiguous: boolean;
  /** The reference value matched no row in the source scope. */
  readonly unmatched?: boolean;
  /** A vacant-seat reference names a Position ID no row in the source scope has. */
  readonly unresolved?: boolean;
}

/**
 * Weak identity strategies whose values are human labels rather than
 * stable source keys. Only these may be re-pointed at another row's
 * entity — external_ref identities already unify by value. Note:
 * `single_field` counts as weak here even though `hasStableSourceKeys`
 * in the binding service treats it as stable — for reference resolution
 * what matters is that the value can name another row's entity, which a
 * single name-like field can.
 */
const REFERENCE_RESOLUTION_STRATEGIES: ReadonlySet<SourceIdentityRule['strategy']> =
  new Set(['label_match', 'manual_review', 'single_field']);

/**
 * Whether a non-primary mapping in a namespace resolves against the primary
 * rows. Label strategies match labels and identity values; an explicit
 * external reference (a manager-id column) matches identity values only, so
 * a stray id can never adopt a row through its display name. Both kinds are
 * references: an adopted one is an endpoint, never a record of its own.
 */
function isReferencePair(pair: MappingIdentityPair): boolean {
  return REFERENCE_RESOLUTION_STRATEGIES.has(pair.rule.strategy)
    || pair.rule.strategy === 'external_ref';
}

interface MappingIdentityPair {
  readonly mapping: SourceEntityMapping;
  readonly rule: SourceIdentityRule;
}

/**
 * Resolves same-file entity references before minting external IDs.
 *
 * Profiles like person_hierarchy materialize both a primary entity per
 * row (person keyed by email) and reference entities named by another
 * row (manager keyed by the "Reports To" name). Without resolution the
 * reference mints a name-keyed external ID that duplicates the
 * referenced row's email-keyed record. This pre-pass matches each weak
 * reference value against the label/identity values of its namespace's
 * primary mapping across the whole reference scope (plus primaries of
 * sibling reference mappings sharing the same source fields, so
 * manager-position can resolve through the person name) and, on a
 * unique match, adopts the referenced row's canonical external ID.
 * Ambiguous matches are not materialized and are reported as
 * `ambiguous_reference` issues. Rows whose primary mapping is missing
 * required fields never donate their external ID — a row that cannot
 * materialize cannot be a merge target.
 *
 * The primary mapping per namespace is the first one declared in
 * entityMappings — profile builders must list the canonical mapping
 * before its reference aliases.
 *
 * Scope notes: `scopeRecords` must cover the whole source snapshot
 * (not one batch) so adoption and ambiguity detection are deterministic
 * regardless of batching. Cross-source identity still flows through
 * identity claims and `same_as` resolution decisions — never rewritten
 * here; a prior human rejection of a stub-vs-canonical `same_as` pair
 * is not consulted (accepted limitation: the file's own reference
 * column is treated as source data, not resolution).
 */
function buildIntraSourceReferenceResolver(
  profile: SourceMappingProfile,
  entityMappings: readonly SourceEntityMapping[],
  scopeRecords: readonly SourceMappingSourceRecord[],
): SourceMappingReferenceResolver {
  const pairs: MappingIdentityPair[] = [];
  for (const mapping of entityMappings) {
    const rule = profile.identityRules.find((candidate) => candidate.entityRef === mapping.id);
    if (rule && rule.fields.length > 0) pairs.push({ mapping, rule });
  }
  const namespaceOf = (pair: MappingIdentityPair): string =>
    pair.rule.identityNamespace ?? pair.mapping.id;

  const primaryByNamespace = new Map<string, MappingIdentityPair>();
  for (const pair of pairs) {
    const namespace = namespaceOf(pair);
    if (!primaryByNamespace.has(namespace)) primaryByNamespace.set(namespace, pair);
  }

  const referencePairs = pairs.filter((pair) =>
    isReferencePair(pair) &&
    primaryByNamespace.get(namespaceOf(pair)) !== pair,
  );
  if (referencePairs.length === 0) return () => null;

  const fieldSignature = (rule: SourceIdentityRule): string => JSON.stringify(rule.fields);
  const namespacesByFieldSignature = new Map<string, Set<string>>();
  for (const pair of referencePairs) {
    const signature = fieldSignature(pair.rule);
    const namespaces = namespacesByFieldSignature.get(signature) ?? new Set<string>();
    namespaces.add(namespaceOf(pair));
    namespacesByFieldSignature.set(signature, namespaces);
  }

  const rowsByNamespaceValue = new Map<string, Map<string, Set<number>>>();
  const rowsByNamespaceIdentity = new Map<string, Map<string, Set<number>>>();
  for (const [namespace, primary] of primaryByNamespace) {
    const labelFields = primary.mapping.fieldMappings
      .filter((field) => field.role === 'label')
      .map((field) => field.sourceField);
    const identityFields = [...new Set(primary.rule.fields)];
    const fields = [...new Set([...labelFields, ...identityFields])];
    const valueMap = new Map<string, Set<number>>();
    const identityMap = new Map<string, Set<number>>();
    const index = (target: Map<string, Set<number>>, value: string, rowIndex: number) => {
      const rows = target.get(value) ?? new Set<number>();
      rows.add(rowIndex);
      target.set(value, rows);
    };
    scopeRecords.forEach((record, rowIndex) => {
      if (missingRequiredFields(primary.mapping.fieldMappings, record).length > 0) return;
      for (const field of fields) {
        const value = normalizeReferenceValue(record.data[field]);
        if (!value) continue;
        index(valueMap, value, rowIndex);
        if (identityFields.includes(field)) index(identityMap, value, rowIndex);
      }
    });
    rowsByNamespaceValue.set(namespace, valueMap);
    rowsByNamespaceIdentity.set(namespace, identityMap);
  }

  const referencePairById = new Map(referencePairs.map((pair) => [pair.mapping.id, pair]));
  const memo = new Map<string, IntraSourceReferenceResolution | null>();
  const positionRow = sourcePositionRowContract(profile);
  const adopt = (
    primary: MappingIdentityPair,
    matchedRows: ReadonlySet<number>,
  ): IntraSourceReferenceResolution => {
    const adoptedIds = new Set(
      [...matchedRows].map((row) =>
        entityExternalId(profile, primary.mapping, scopeRecords[row], row),
      ),
    );
    return adoptedIds.size === 1
      ? { externalId: [...adoptedIds][0], ambiguous: false }
      : { externalId: null, ambiguous: true };
  };

  return (entityMappingId, sourceRecord) => {
    const pair = referencePairById.get(entityMappingId);
    if (!pair) return null;
    const primary = primaryByNamespace.get(namespaceOf(pair));
    if (!primary) return null;
    const value = pair.rule.fields
      .map((field) => normalizeReferenceValue(sourceRecord.data[field]))
      .find((candidate): candidate is string => candidate !== null);
    if (!value) return null;

    const memoKey = `${entityMappingId} ${value}`;
    const cached = memo.get(memoKey);
    if (cached !== undefined) return cached;

    // A vacant-seat reference names its seat by Position ID, so it matches
    // that ID directly rather than any incumbent cell. The seat's own row may
    // carry the reference or a blank incumbent.
    const vacantPositionId = positionRow && primary.mapping.ontologyType === 'position'
      ? vacantSeatReferencePositionId(value)
      : null;
    if (vacantPositionId) {
      const matchedRows = rowsByNamespaceIdentity.get(namespaceOf(pair))?.get(vacantPositionId);
      const resolution: IntraSourceReferenceResolution = matchedRows?.size
        ? adopt(primary, matchedRows)
        : { externalId: null, ambiguous: false, unresolved: true };
      memo.set(memoKey, resolution);
      return resolution;
    }

    const candidateNamespaces =
      namespacesByFieldSignature.get(fieldSignature(pair.rule)) ??
      new Set([namespaceOf(pair)]);
    const rowsByValue = pair.rule.strategy === 'external_ref'
      ? rowsByNamespaceIdentity
      : rowsByNamespaceValue;
    const matchedRows = new Set<number>();
    for (const candidateNamespace of candidateNamespaces) {
      for (const row of rowsByValue.get(candidateNamespace)?.get(value) ?? []) {
        matchedRows.add(row);
      }
    }
    const resolution: IntraSourceReferenceResolution = matchedRows.size > 0
      ? adopt(primary, matchedRows)
      : { externalId: null, ambiguous: false, unmatched: true };
    memo.set(memoKey, resolution);
    return resolution;
  };
}

function normalizeReferenceValue(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const raw = String(value).trim().toLowerCase();
  if (!raw) return null;
  return raw.includes('@') ? raw.replace(/\s+/g, '') : raw.replace(/\s+/g, ' ');
}

function identityClaimsForEntity(input: {
  profile: SourceMappingProfile;
  entityMapping: SourceEntityMapping;
  sourceRecord: SourceMappingSourceRecord;
  externalId: string;
  label: string;
}): SourceMappingIdentityClaim[] {
  return input.profile.identityRules
    .filter((rule) => rule.entityRef === input.entityMapping.id)
    .map((rule): SourceMappingIdentityClaim | null => {
      if (rule.fields.length === 0) return null;
      const values = rule.fields
        .map((field) => sourceValue(input.sourceRecord, field))
        .map((value) => String(value ?? '').trim());
      if (values.some((value) => value.length === 0)) return null;
      return {
        ruleId: rule.id,
        strategy: rule.strategy,
        sourceSystem: input.profile.sourceSystem,
        intent: input.profile.intent,
        sourceRecordId: input.sourceRecord.id,
        entityRef: input.entityMapping.id,
        entityType: input.entityMapping.ontologyType,
        profileId: input.entityMapping.profileId ?? input.profile.targetProfileId,
        entityExternalId: input.externalId,
        label: input.label,
        fields: [...rule.fields],
        values,
        ...(rule.targetProfileId ? { targetProfileId: rule.targetProfileId } : {}),
        confidence: rule.confidence ?? defaultIdentityConfidence(rule.strategy),
        ...(input.entityMapping.referenceOnly ? { referenceOnly: true } : {}),
      };
    })
    .filter((claim): claim is SourceMappingIdentityClaim => claim !== null);
}

function defaultIdentityConfidence(
  strategy: SourceIdentityRule['strategy'],
): number {
  switch (strategy) {
    case 'external_ref':
      return 0.98;
    case 'single_field':
      return 0.9;
    case 'composite_fields':
      return 0.86;
    case 'label_match':
      return 0.65;
    case 'manual_review':
      return 0.4;
  }
}

function entityLabel(
  entityMapping: SourceEntityMapping,
  sourceRecord: SourceMappingSourceRecord,
  props: Record<string, unknown>,
  fallback: string,
): string {
  const mappedName = readPath(props, 'name');
  if (typeof mappedName === 'string' && mappedName.trim()) return mappedName.trim();

  const labelField = entityMapping.fieldMappings.find((field) => field.role === 'label');
  const labelValue = labelField ? sourceValue(sourceRecord, labelField.sourceField) : null;
  if (labelValue != null && String(labelValue).trim()) return String(labelValue).trim();

  return fallback;
}

function mergeMaterializedRecord(
  existing: SourceMappingMaterializedRecord | undefined,
  next: SourceMappingMaterializedRecord,
): SourceMappingMaterializedRecord {
  if (!existing) return next;
  return {
    externalId: next.externalId,
    profileId: next.profileId ?? existing.profileId,
    tombstonePolicy: next.tombstonePolicy ?? existing.tombstonePolicy,
    universalProps: {
      ...existing.universalProps,
      ...next.universalProps,
      source: mergeSourceMetadata(existing.universalProps.source, next.universalProps.source),
    },
  };
}

function conflictingRelationIssue(
  sourceRecordId: string,
  mappingId: string,
): SourceMappingUnresolvedIssue {
  return {
    sourceRecordId,
    mappingId,
    kind: 'relation',
    reason: 'conflicting_duplicate',
  };
}

export function mergeDuplicateRelationshipRecord(
  existing: SourceMappingMaterializedRecord | undefined,
  next: SourceMappingMaterializedRecord,
): SourceMappingMaterializedRecord {
  if (!existing) return next;
  // Exact endpoint/qualifier duplicates represent one fact. Pick provenance
  // deterministically so reordering identical CSV rows cannot rewrite the
  // protected value or its source-record receipt.
  return materializedSourceRecordId(existing).localeCompare(materializedSourceRecordId(next)) <= 0
    ? existing
    : next;
}

function materializedSourceRecordId(record: SourceMappingMaterializedRecord): string {
  const source = isRecord(record.universalProps.source)
    ? record.universalProps.source
    : null;
  const value = source?.recordId;
  return typeof value === 'string' && value.trim()
    ? value
    : record.externalId ?? 'unknown-source-row';
}

export function sameRelationshipSemantics(
  left: SourceMappingMaterializedRecord,
  right: SourceMappingMaterializedRecord,
): boolean {
  return stableJson(relationshipSemantics(left.universalProps))
    === stableJson(relationshipSemantics(right.universalProps));
}

function relationshipSemantics(
  props: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const ignored = new Set(['source', 'name', '_name', 'subjectLabel', 'objectLabel']);
  return Object.fromEntries(Object.entries(props).filter(([key]) => !ignored.has(key)));
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (isRecord(value)) {
    return `{${Object.keys(value).sort().map((key) =>
      `${JSON.stringify(key)}:${stableJson(value[key])}`
    ).join(',')}}`;
  }
  return JSON.stringify(value);
}

function mergeSourceMetadata(existing: unknown, next: unknown): unknown {
  if (!isRecord(existing) || !isRecord(next)) return next;
  return {
    ...existing,
    ...next,
    rowSnapshot: next.rowSnapshot ?? existing.rowSnapshot,
  };
}

function stripSource(attributes: Record<string, unknown>): Record<string, unknown> {
  const qualifiers = { ...attributes };
  delete qualifiers.source;
  return qualifiers;
}

function normalizeRelationshipQualifiers(
  qualifiers: Record<string, unknown>,
): Record<string, unknown> {
  const normalized: Record<string, unknown> = { ...qualifiers };
  if ('allocation' in qualifiers) {
    const allocation = normalizeAllocation(qualifiers.allocation);
    if (allocation != null) normalized.allocation = allocation;
    normalized.sourceAllocation = qualifiers.allocation;
  }
  return normalized;
}

function normalizeAllocation(value: unknown): number | null {
  if (value == null || value === '') return null;
  const trimmed = typeof value === 'string' ? value.trim() : null;
  const hasPercentSuffix = trimmed?.endsWith('%') ?? false;
  const raw = trimmed ? trimmed.replace(/%$/, '') : value;
  const numeric = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(numeric)) return null;
  const ratio = hasPercentSuffix || numeric > 10 ? numeric / 100 : numeric;
  return Math.max(0, ratio);
}

function sourceValue(sourceRecord: SourceMappingSourceRecord, field: string): unknown {
  return sourceRecord.data[field];
}

function missingRequiredFields(
  fieldMappings: readonly SourceFieldMapping[],
  sourceRecord: SourceMappingSourceRecord,
): string[] {
  return fieldMappings
    .filter((fieldMapping) =>
      fieldMapping.required && isBlankSourceValue(sourceValue(sourceRecord, fieldMapping.sourceField)))
    .map((fieldMapping) => fieldMapping.sourceField);
}

function isBlankSourceValue(value: unknown): boolean {
  return value == null || (typeof value === 'string' && value.trim().length === 0) || value === '';
}

function isOptionalEntityMapping(
  profile: SourceMappingProfile,
  positionRow: SourcePositionRowContract | null,
  mapping: SourceEntityMapping,
): boolean {
  // Preserve compatibility with position-hierarchy mappings reviewed before
  // vacant-seat and root-position semantics were encoded explicitly.
  return mapping.optionalWhenBlank
    || positionRow?.occupantMapping.id === mapping.id
    || profile.intent === 'position_hierarchy' && mapping.id === 'manager-position';
}

function isOptionalEntityAbsent(
  profile: SourceMappingProfile,
  positionRow: SourcePositionRowContract | null,
  mapping: SourceEntityMapping,
  sourceRecord: SourceMappingSourceRecord,
): boolean {
  if (!isOptionalEntityMapping(profile, positionRow, mapping)) return false;
  const fields = positionRow?.occupantMapping.id === mapping.id
    ? positionRow.occupantPresenceFields
    : mapping.fieldMappings.map((field) => field.sourceField);
  // A vacant-seat reference stands in for nobody: neither an incumbent nor a
  // manager person, while the seat mappings still resolve it.
  const vacantMeansAbsent = Boolean(positionRow)
    && ['actor', 'org_person', 'person'].includes(mapping.ontologyType);
  return fields.every((field) => {
    const value = sourceValue(sourceRecord, field);
    return isBlankSourceValue(value)
      || (vacantMeansAbsent && vacantSeatReferencePositionId(value) !== null);
  });
}

function isOptionalRelationSource(
  positionRow: SourcePositionRowContract | null,
  mapping: SourceRelationMapping,
): boolean {
  return mapping.optionalSource
    || positionRow?.occupancyMapping.id === mapping.id;
}

function isOptionalRelationTarget(
  profile: SourceMappingProfile,
  mapping: SourceRelationMapping,
): boolean {
  return mapping.optionalTarget
    || profile.intent === 'position_hierarchy' && mapping.predicate === 'reports_to';
}

function writeMappedValue(
  target: Record<string, unknown>,
  fieldMapping: SourceFieldMapping,
  value: unknown,
  rootPrefix: 'props' | 'qualifiers',
): void {
  const prefix = `${rootPrefix}.`;
  const path = fieldMapping.targetPath.startsWith(prefix)
    ? fieldMapping.targetPath.slice(prefix.length)
    : fieldMapping.targetPath;
  writePath(target, path, value);
}

function readPath(target: Record<string, unknown>, path: string): unknown {
  let cursor: unknown = target;
  for (const segment of path.split('.')) {
    if (!isRecord(cursor)) return undefined;
    cursor = cursor[segment];
  }
  return cursor;
}

function writePath(target: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path.split('.').filter(Boolean);
  if (segments.length === 0) return;
  let cursor: Record<string, unknown> = target;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const segment = segments[i];
    const next = cursor[segment];
    if (!isRecord(next)) {
      cursor[segment] = {};
    }
    cursor = cursor[segment] as Record<string, unknown>;
  }
  cursor[segments[segments.length - 1]] = value;
}

function hasString(target: Record<string, unknown>, path: string): boolean {
  const value = readPath(target, path);
  return typeof value === 'string' && value.trim().length > 0;
}

function edgeRecordExternalId(input: SourceMappingMaterializeInput): string {
  return [
    safeSegment(input.mappingProfile.sourceSystem),
    safeSegment(input.mappingProfile.intent),
    'edges',
    stableSegment(input.sourceLabel ?? input.connectorId ?? input.mappingProfile.id),
  ].join(':');
}

function relationshipExternalId(
  input: SourceMappingMaterializeInput,
  edge: MaterializedEdge,
): string {
  return [
    safeSegment(input.mappingProfile.sourceSystem),
    safeSegment(input.mappingProfile.intent),
    'relationship',
    safeSegment(edge.kind),
    stableSegment(edge.id),
  ].join(':');
}

function stableSegment(value: unknown): string {
  const raw = String(value ?? '').trim();
  const safe = safeSegment(raw).slice(0, 60);
  return `${safe || 'value'}-${hashString(raw)}`;
}

/**
 * Stable, hash-suffixed identity segment used as the final `:`-separated part
 * of materialized entity externalIds (e.g. the e-mail-derived
 * `aisha-patel-fernhill-io-1sdavx9`). Exported so consumers holding the raw
 * identity value (canvas record linking) can re-derive the segment without
 * access to redacted identity props.
 */
export function stableExternalIdSegment(value: unknown): string {
  return stableSegment(value);
}

function safeSegment(value: unknown): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-')
    || 'unknown';
}

function hashString(value: string): string {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function humanizeIdentifier(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
